import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MenuService } from '../service/menu.service';
import { PrincipleEmployerService } from '../service/principle-employer.service';
import { UserService } from '../service/user.service';
import { ChallanActivityTitleService } from '../service/challan-activity-title.service';
import { NavigationConfigService, NavigationTab_Dto } from '../service/navigation-config.service';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.css'],
})
export class TabsComponent implements OnInit, OnDestroy {
  role: any;
  selectedTab?: string;
  selectedSidebar!: string;
  clraType: any;
  showTabs: boolean = true;
  currentRoute: string = this.router.url;
  mockTabs: any = [];
  selectedModuleName: string = '';
  // Full Act name (e.g. "Employee Provident Fund Act") that REPLACES the "Challan Activity"
  // tab label to reflect the type selected inside the Challan Activity screens.
  // Fed by ChallanActivityTitleService.
  challanTitle: string = '';

  // Closes every long-lived subscription in ngOnInit. This component sits inside an
  // *ngIf in the layout, so it is destroyed and recreated as the user moves around;
  // without a teardown each instance's observers stayed registered on the root-scoped
  // services for the life of the tab, and the whole component graph stayed reachable
  // through them.
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly menuService: MenuService,
    private readonly PEService: PrincipleEmployerService,
    private readonly user: UserService,
    private readonly challanTitleService: ChallanActivityTitleService,
    private readonly navConfigService: NavigationConfigService
  ) {}

  navigateToMock(component: string) {
    this.selectedTab = component;
    this.router.navigate([`/${component}`]);
  }

  isTabSelectedMock(component: string): boolean {
      this.currentRoute = component;
    return this.selectedTab === component;
  }

  ngOnInit(): void {
    
    this.currentRoute = this.router.url;
    // Reflect the Challan Activity type in the tab label — the selected Act's full name
    // (e.g. "Employee Provident Fund Act") replaces "Challan Activity".
    this.challanTitleService.title$
      .pipe(takeUntil(this.destroy$))
      .subscribe((title) => {
        this.challanTitle = title;
      });
    this.role = this.user.getRole();
    if (this.user.getClientId()) {
      this.getClraType();
    }

    this.menuService.selectedMenu$
      .pipe(takeUntil(this.destroy$))
      .subscribe((menu) => {
        this.selectedSidebar = menu || '';
        // Tabs come from the server-side, role-scoped navigation config (the old
        // client-side tabData.ts exposed every role's tabs in the bundle — VAPT).
        // Role filtering already happened on the API from the JWT; only the module
        // is filtered here.
        //
        // Prefer the cache: authRouteGuard fetches the config and AWAITS it before it
        // activates a protected route, so by the time this component exists the config is
        // already there and the tabs render synchronously — no request, and no failure
        // mode. Only fall back to a fetch if the cache is somehow cold, and only when
        // there is a token to send: GetNavigationConfig is [Authorize], and calling it
        // unauthenticated would 401 and end in a forced logout.
        const cached = this.navConfigService.getConfigSync();
        if (cached) {
          this.applyTabs(cached.tabs);
          return;
        }
        if (!this.user.getAccessToken()) {
          // No session yet — leave the current tabs alone rather than blanking the bar.
          // selectedMenu$ emits again on the next sidebar click, by which point the
          // session exists and the cache is warm.
          return;
        }
        this.navConfigService.getConfig()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (config) => {
              this.applyTabs(config.tabs);
            },
            error: () => {
              this.mockTabs = [];
            }
          });
      });

    // Subscribed ONCE, at the top level. This handler never used the emitted menu, but
    // living inside the selectedMenu$ callback meant a further permanent NavigationEnd
    // subscription was added on every sidebar click — so after N clicks the same work
    // ran N times per navigation. selectedMenu$ is a BehaviorSubject that emits
    // synchronously on subscribe, so the handler is still registered during this same
    // ngOnInit and the visible behaviour is identical.
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.selectedTab = event.urlAfterRedirects.slice(1);
          this.showTabs = !(
            event.urlAfterRedirects.includes('PremiseAddEdit') ||
            event.urlAfterRedirects.includes('LocationAddEdit') ||
            event.urlAfterRedirects.includes('ContractorAddEdit') ||
            event.urlAfterRedirects.includes('PremiseBulkUpload') ||
            event.urlAfterRedirects.includes('RegistrationRepositoryAddEdit') ||
            event.urlAfterRedirects.includes('AddRegistrationRepository') ||
            // event.urlAfterRedirects.includes('PFRegistrations') ||
            // event.urlAfterRedirects.includes('ESICRegistrations') ||
            // event.urlAfterRedirects.includes('PTRegistrations') ||
            // event.urlAfterRedirects.includes('LWFRegistrations') ||
            // event.urlAfterRedirects.includes('NFHRegistrations') ||
            event.urlAfterRedirects.includes('ListofHolidaysNotifications') ||
            event.urlAfterRedirects.includes('ChallanActivityEdit')
          );
        }
      });
  }

  /** Build the tab bar for the currently selected module and pick the active tab. */
  private applyTabs(tabs: NavigationTab_Dto[]): void {
    this.mockTabs = tabs
      .filter((t) => t.moduleName === this.selectedSidebar)
      .map((t) => ({ tab: t.tabName, component: t.componentRoute, sort: t.sortOrder }));

    this.selectedTab =
      this.mockTabs.find(
        (t: any) =>
          t.component.toLowerCase() ===
          this.router.url.split('/').pop()?.toLowerCase()
      )?.component || this.mockTabs[0]?.component;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  Refresh() {
    window.location.reload();
  }
  getClraType() {
    const clientId = this.user.getClientId();
    this.PEService.getClraType(clientId).subscribe({
      next: (response) => {
        const data = response.body.result;
        this.clraType = data;
      },
    });
  }

  // fetchTabs(menu: string): void {
  //   this.tabsService.getTabsDetails(menu).subscribe({
  //     next: (response) => {
  //       if (response) {
  //         this.tabs = response.body;
  //         this.sortedTabs = this.tabs
  //           .filter(tab => {
  //             if ((this.role === 'PERF' || this.role==='RVW1') && this.selectedSidebar === 'Onboard Entity' ) {
  //               return tab.tabName === 'Premises' || tab.tabName === 'User-Branch Mapping';
  //             }
  //             return true;
  //           })
  //           .sort((a, b) => a.sort_order - b.sort_order)
  //           .filter((tab) => {
  //             return !(this.selectedSidebar === 'Onboard CLRA' && this.clraType === 'CAP' && tab.tabName === 'Employee Mapping');
  //           });

  //         const initialTab = this.tabs.find(tab => tab.routePath === this.router.url);
  //         if (initialTab) {
  //           this.selectedTab = initialTab.tabName;
  //           this.applySelectedTabClass();
  //         }
  //       }
  //     },
  //     error: (error) => {
  //     }
  //   });
  // }

  // navigateTo(tab: Tab): void {
  //   this.selectedTab = tab.tabName;
  //   this.router.navigate([tab.routePath]);
  //   this.applySelectedTabClass();
  // }

  // isTabSelected(tab: Tab): boolean {
  //   return this.selectedTab === tab.tabName;

  // }

  applySelectedTabClass(): void {
    setTimeout(() => {
      const elements = document.querySelectorAll('.listItem');
      elements.forEach((element: Element) => {
        if (element.innerHTML.includes(this.selectedTab ?? '')) {
          this.renderer.addClass(element, 'selected-tab');
        } else {
          this.renderer.removeClass(element, 'selected-tab');
        }
      });
    }, 0);
  }
}
